import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-navy-dark text-white py-6 border-b border-gray-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-teal-400 mb-4 -ml-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Home
          </Button>
          <h1 className="text-3xl font-bold">Privacy Policy</h1>
          <p className="text-gray-400 mt-2">Last updated: 01.12.25</p>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="prose prose-lg max-w-none">
          
          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Who we are</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Weight Prescriber Network ("we", "us", "our") provides pharmacist-led prescribing capacity services to regulated UK weight management providers (B2B).
          </p>
          <p className="text-gray-700 leading-relaxed mb-2"><strong>Data controller:</strong></p>
          <ul className="list-none ml-0 mb-6 text-gray-700">
            <li>Company name: PHARMKG LTD</li>
            <li>Company number: 14702569</li>
            <li>Registered/operating address: 261 High Street, Waltham Cross, EN8 7BE, United Kingdom</li>
            <li>Email: info@weightprescribernetwork.co.uk</li>
          </ul>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">What this policy covers</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            This Privacy Policy explains how we collect, use, store and share personal data when you:
          </p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Visit our website</li>
            <li>Submit an enquiry via our contact form</li>
            <li>Contact us by email</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            This website is intended for business users. It is not intended for patients, and we do not request patient medical information through the website.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Personal data we collect</h2>
          <p className="text-gray-700 leading-relaxed mb-4">We may collect the following personal data:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li><strong>Identity data:</strong> your name</li>
            <li><strong>Business contact data:</strong> company name, job title/role, work email address</li>
            <li><strong>Enquiry data:</strong> the information you submit in your message and any details you choose to provide</li>
            <li><strong>Technical data:</strong> device information, browser type, IP address, and basic usage data (depending on site configuration)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6 font-semibold">
            Important: Please do not include special category data (e.g., patient medical information) in the contact form.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">How we use your personal data</h2>
          <p className="text-gray-700 leading-relaxed mb-4">We use your personal data to:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Respond to enquiries and communicate with you</li>
            <li>Provide information about our services and arrange a confidential discussion</li>
            <li>Maintain internal records of business enquiries</li>
            <li>Improve and secure our website (including preventing fraud and misuse)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6 font-semibold">
            We do not sell your personal data.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Lawful bases for processing</h2>
          <p className="text-gray-700 leading-relaxed mb-4">Under UK GDPR, we rely on the following lawful bases:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li><strong>Legitimate interests:</strong> to respond to B2B enquiries, operate and improve our website, and manage business relationships</li>
            <li><strong>Contract:</strong> where processing is necessary to take steps at your request prior to entering into a contract, or to perform a contract</li>
            <li><strong>Legal obligation:</strong> where we must comply with applicable laws</li>
            <li><strong>Consent:</strong> where required for certain cookies/analytics or marketing communications (if enabled)</li>
          </ul>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Marketing</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may contact you with service-related communications following an enquiry.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            If we send marketing communications, we will do so in line with applicable UK rules, and you can opt out at any time by emailing info@weightprescribernetwork.co.uk.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Sharing your personal data</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may share personal data with trusted service providers who support our operations, such as:
          </p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Website hosting providers</li>
            <li>Email and communication providers</li>
            <li>Security and monitoring providers</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-4">
            These providers are required to protect your data and only process it on our instructions.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            We may disclose data where required by law or to protect our rights.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">International transfers</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Some providers may process data outside the UK. Where this happens, we ensure appropriate safeguards are in place (such as the UK International Data Transfer Agreement or other recognised safeguards).
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Data retention</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We keep enquiry data only for as long as necessary: Typically, up to 12-24 months after the last meaningful contact, unless a longer period is required for legal or contractual reasons.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Security</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We implement reasonable technical and organisational measures to protect personal data. No method of transmission over the internet is completely secure, but we take steps to reduce risk.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Your rights</h2>
          <p className="text-gray-700 leading-relaxed mb-4">You have rights under UK data protection law, including:</p>
          <ul className="list-disc ml-6 mb-6 text-gray-700">
            <li>Access to your personal data</li>
            <li>Correction of inaccurate data</li>
            <li>Erasure (in certain circumstances)</li>
            <li>Restriction or objection to processing (in certain circumstances)</li>
            <li>Data portability (in certain circumstances)</li>
          </ul>
          <p className="text-gray-700 leading-relaxed mb-6">
            To exercise your rights, email info@weightprescribernetwork.co.uk.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Complaints</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            If you have concerns, contact us first and we will try to resolve them.
          </p>
          <p className="text-gray-700 leading-relaxed mb-2">
            You also have the right to complain to the Information Commissioner's Office (ICO):
          </p>
          <ul className="list-none ml-0 mb-6 text-gray-700">
            <li>ICO website: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:text-teal-800 underline">ico.org.uk</a></li>
            <li>ICO registration number: 00018850446</li>
          </ul>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Cookies</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            We may use cookies and similar technologies to operate the website and (if enabled) measure performance.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            If analytics/cookies are enabled, we will display a cookie notice and, where required, obtain consent.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            You can manage cookies through your browser settings.
          </p>

          <h2 className="text-2xl font-bold text-navy-dark mt-8 mb-4">Changes to this Privacy Policy</h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            We may update this policy from time to time. The latest version will be posted on this page with the "Last updated" date.
          </p>

        </div>

        <div className="mt-12 pt-8 border-t border-gray-200">
          <Button
            onClick={() => navigate('/')}
            className="bg-teal-700 hover:bg-teal-800 text-white"
          >
            <ArrowLeft className="mr-2" size={18} />
            Return to Home
          </Button>
        </div>
      </div>
    </div>
  );
};
